import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

/**
 * Composant reCAPTCHA réutilisable
 * 
 * @component
 * @example
 * const recaptchaRef = useRef(null);
 * 
 * <ReCaptcha
 *   ref={recaptchaRef}
 *   onChange={(token) => setRecaptchaToken(token)}
 *   onExpired={() => setRecaptchaToken(null)}
 *   theme="light"
 * />
 */
const ReCaptcha = forwardRef((props, ref) => {
  const { 
    onChange, 
    onExpired, 
    onError,
    theme = 'light',
    size = 'normal',
    hl = 'fr'
  } = props;
  
  const recaptchaRef = useRef(null);
  const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY;
  
  // Exposer les méthodes au parent via ref
  useImperativeHandle(ref, () => ({
    /**
     * Récupère le token reCAPTCHA actuel
     * @returns {string|null} Token ou null
     */
    getValue: () => {
      return recaptchaRef.current?.getValue() || null;
    },
    
    /**
     * Réinitialise le reCAPTCHA
     */
    reset: () => {
      recaptchaRef.current?.reset();
    },
    
    /**
     * Exécute le reCAPTCHA (pour invisible)
     */
    execute: () => {
      recaptchaRef.current?.execute();
    }
  }));
  
  // Vérifier que la clé est configurée
  if (!siteKey) {
    console.error('❌ REACT_APP_RECAPTCHA_SITE_KEY non configurée dans .env');
    return (
      <div className="text-red-600 text-sm p-3 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
        <p className="font-semibold">⚠️ reCAPTCHA non configuré</p>
        <p className="text-xs mt-1">
          Veuillez contacter l'administrateur système.
        </p>
      </div>
    );
  }
  
  const handleChange = (token) => {
    if (onChange) {
      onChange(token);
    }
  };
  
  const handleExpired = () => {
    if (onExpired) {
      onExpired();
    }
  };
  
  const handleError = () => {
    if (onError) {
      onError();
    }
  };
  
  return (
    <div className="flex justify-center my-4">
      <ReCAPTCHA
        ref={recaptchaRef}
        sitekey={siteKey}
        onChange={handleChange}
        onExpired={handleExpired}
        onErrored={handleError}
        theme={theme}
        size={size}
        hl={hl}
      />
    </div>
  );
});

ReCaptcha.displayName = 'ReCaptcha';

export default ReCaptcha;
